import { supabase } from 'utils/supabase'
import ApiService from './ApiService'
import { AI_PROMPTS_TABLE } from 'constants/db.contants'
import appConfig from 'configs/app.config'

export async function apiSignIn(payload) {
    return await supabase.auth.signInWithPassword(payload)
}

export async function apiGetUserSession() {
    return await supabase.auth.getSession()
}

export async function apiValidateAuth() {
    return ApiService.fetchData({
        url: '/auth/validate',
        method: 'get',
    })
}

export async function apiGetEulaStatus(token) {
    const response = await fetch(`${appConfig.apiPrefix}/auth/eula-status`, {
        method: 'GET',
        headers: {
            Authorization: `Bearer ${token}`,
            'Content-Type': 'application/json',
            'ngrok-skip-browser-warning': 'true',
        },
    })

    const data = await response.json()

    if (!response.ok) {
        throw new Error(data?.message || 'Failed to fetch EULA status')
    }

    return data
}

export async function apiAcceptEula(token) {
    const response = await fetch(`${appConfig.apiPrefix}/auth/eula-accept`, {
        method: 'POST',
        headers: {
            Authorization: `Bearer ${token}`,
            'Content-Type': 'application/json',
            'ngrok-skip-browser-warning': 'true',
        },
    })

    const data = await response.json()

    if (!response.ok) {
        throw new Error(data?.message || 'Failed to accept EULA')
    }

    return data
}

export async function apiSignUp(data) {
    return ApiService.fetchData({
        url: '/sign-up',
        method: 'post',
        data,
    })
}

export async function apiSignOut() {
    return await supabase.auth.signOut()
}

// --- Forgot / reset password (backend: /auth/*) — use only these helpers, not raw axios elsewhere ---

/**
 * Request password reset email (mediator must exist in DB). Backend sends Loops email.
 * @param {{ email: string }} data
 */
export async function apiForgotPassword(data) {
    return ApiService.fetchData({
        url: '/auth/forgot-password',
        method: 'post',
        data,
    })
}

/**
 * Validate reset token from email link before showing the new-password form.
 * @param {string} token — JWT from `?token=` on `/reset-password`
 */
export async function apiValidateResetToken(token) {
    return ApiService.fetchData({
        url: '/auth/reset-password/validate',
        method: 'get',
        params: { token },
    })
}

/**
 * Submit new password after token validation.
 * @param {{ token: string, password: string, confirmPassword: string }} data
 */
export async function apiResetPassword(data) {
    return ApiService.fetchData({
        url: '/auth/reset-password',
        method: 'post',
        data,
    })
}

export async function apiStripeConnectUrl(data) {
    return ApiService.fetchData({
        url: '/stripe/create-stripe-account',
        method: 'post',
        data,
    })
}

export async function apiSyncStripeDetails() {
    return ApiService.fetchData({
        url: '/stripe/sync-details',
        method: 'post',
    })
}

export async function apiValidateCoupon(data) {
    return ApiService.fetchData({
        url: '/coupon/validate-coupon',
        method: 'post',
        data,
    })
}

export async function apiValidateSpecificSlotCoupon(data) {
    return ApiService.fetchData({
        url: '/coupon/validate-specific-slot-coupon',
        method: 'post',
        data,
    })
}

export async function apiCreateCoupon(data) {
    return ApiService.fetchData({
        url: '/coupon/create',
        method: 'post',
        data,
    })
}

export async function apiDeleteCoupon(couponId) {
    return ApiService.fetchData({
        url: `/coupon/delete/${couponId}`,
        method: 'delete',
    })
}

export async function apiUpdateCoupon(id, mediator, data) {
    return ApiService.fetchData({
        url: `/coupon/update/${id}/${mediator}`,
        method: 'put',
        data,
    })
}

export async function apiGetAllCoupons(mediatorId, show_all = true) {
    const data = await ApiService.fetchData({
        url: `/coupon/get-all/?mediator_id=${mediatorId}&show_all=${show_all}`,
        method: 'get',
    })
    // const coupons = data?.data?.coupons?.filter(
    //     (x) => x.coupon_type === 'fixed'
    // )

    // return { data: { coupons } }

    return data
}

export async function apiGoogleConnectAuth() {
    return ApiService.fetchData({
        url: '/google/connect-google-auth',
        method: 'get',
    })
}

export async function apiOutlookConnectAuth() {
    return ApiService.fetchData({
        url: '/outlook/connect-outlook-auth',
        method: 'get',
    })
}

export async function apiPasswordChange(data) {
    return ApiService.fetchData({
        url: '/admin/password/edit',
        method: 'post',
        data,
    })
}

export async function apiCurrentPassword(id) {
    return ApiService.fetchData({
        url: `/admin/mediator/${id}`,
        method: 'get',
    })
}

/**
 * Legacy: (invoice_id, mediator_user_id, type)
 */
export async function apiGenerateStripeApi(invoice_id, uuid, type) {
    return ApiService.fetchData({
        url: `/stripe/payment-link`,
        method: 'post',
        data: {
            invoice_id: invoice_id,
            mediator_id: uuid,
            type,
        }
    })
}

/**
 * Legacy: (invoice_id, mediator_id, method)
 * Gateway: ({ payment_public_uuid, mediator_id, method })
 */
export async function apiFinalizeInvoicePaymentMethod(
    invoice_id,
    mediator_id,
    method
) {
    if (
        invoice_id &&
        typeof invoice_id === 'object' &&
        !Array.isArray(invoice_id) &&
        'payment_public_uuid' in invoice_id
    ) {
        const p = invoice_id
        return ApiService.fetchData({
            url: `/stripe/invoice/finalize-payment-method`,
            method: 'post',
            data: {
                payment_public_uuid: p.payment_public_uuid,
                mediator_id: p.mediator_id,
                method: p.method,
            }
        })
    }
    return ApiService.fetchData({
        url: `/stripe/invoice/finalize-payment-method`,
        method: 'post',
        data: {
            invoice_id,
            mediator_id,
            method,
        }
    })
}

export async function apiGetPaymentGatewayLink(data) {
    return ApiService.fetchData({
        url: `/stripe/payment-gateway/link`,
        method: 'post',
        data,
        skipAuth: true,
    })
}

export async function apiFinalizePaymentGatewayMethod(data) {
    return ApiService.fetchData({
        url: `/stripe/payment-gateway/finalize-payment-method`,
        method: 'post',
        data,
        skipAuth: true,
    })
}

/**
 * Get envelope access for a case and client
 * @param {string} case_id - The case ID
 * @param {string} client_id - The client ID
 * @returns {Promise} API response with redirectUrl for DocuSign or Zoom
 * 
 * Response format:
 * - Success: { success: true, redirectUrl: string, status: 'completed'|'created', message: string, envelopeId: string, caseId: string, partyId: string }
 * - Error: { success: false, error: string }
 */
export async function apiGetEnvelopeAccess(case_id, client_id) {
    return ApiService.fetchData({
        url: `/case/envelope-access/${case_id}/${client_id}`,
        method: 'get',
    })
}

// AI Prompts Related API - Using Supabase Client
export async function apiCreateAiPrompt(data) {
    try {
        const { data: promptData, error } = await supabase
            .from(AI_PROMPTS_TABLE)
            .insert({
                mediator_id: data.mediator_id,
                name: data.name,
                prompt: data.prompt,
            })
            .select()
            .single()

        if (error) {
            throw error
        }

        return { data: { data: promptData } }
    } catch (error) {
        console.error('Error creating AI prompt:', error)
        throw error
    }
}

export async function apiDeleteAiPrompt(promptId) {
    try {
        const { error } = await supabase
            .from(AI_PROMPTS_TABLE)
            .delete()
            .eq('id', promptId)

        if (error) {
            throw error
        }

        return { success: true }
    } catch (error) {
        console.error('Error deleting AI prompt:', error)
        throw error
    }
}

export async function apiUpdateAiPrompt(id, mediator, data) {
    try {
        const { data: updatedData, error } = await supabase
            .from(AI_PROMPTS_TABLE)
            .update({
                name: data.name,
                prompt: data.prompt,
            })
            .eq('id', id)
            .eq('mediator_id', mediator)
            .select()
            .single()

        if (error) {
            throw error
        }

        return { data: { data: updatedData } }
    } catch (error) {
        console.error('Error updating AI prompt:', error)
        throw error
    }
}

export async function apiGetAllAiPrompts(mediatorId) {
    try {
        const { data: prompts, error } = await supabase
            .from(AI_PROMPTS_TABLE)
            .select('*')
            .eq('mediator_id', mediatorId)
            .order('created_at', { ascending: false })

        if (error) {
            throw error
        }

        return { data: { prompts: prompts || [] } }
    } catch (error) {
        console.error('Error fetching AI prompts:', error)
        throw error
    }
}
